/**
 * Authentication Service
 */
import { getApiClient } from '../apiClient';
import { AUTH_ENDPOINTS } from '../endpoints';
import type { ApiResponse } from '../types';
import type {
  LoginPayload,
  LoginResponse,
  SendOtpPayload,
  SendOtpResponse,
  VerifyOtpPayload,
  VerifyOtpResponse,
  UpdateProfilePayload,
  UpdateProfileResponse,
  GetProfileResponse,
  ResetChangePasswordPayload,
  ResetChangePasswordResponse,
  ForgotPasswordSendOtpPayload,
  ForgotPasswordVerifyOtpPayload,
  ResetPasswordPayload,
  ResetPasswordResponse,
  ChangePasswordPayload,
  ChangePasswordResponse,
} from '../types/auth.types';
import { OTP_TYPE } from '../../constants/otp';

export const authService = {
  /**
   * Get user profile
   */
  getProfile: (): Promise<GetProfileResponse> => {
    return getApiClient().get(AUTH_ENDPOINTS.getProfile);
  },

  /**
   * User login
   */
  login: (body: LoginPayload): Promise<LoginResponse> => {
    return getApiClient().post(AUTH_ENDPOINTS.login, body);
  },

  /**
   * User logout
   */
  logout: (): Promise<ApiResponse<void>> => {
    return getApiClient().get<void>(AUTH_ENDPOINTS.logout);
  },

  /**
   * Send OTP
   */
  sendOtp: (body: SendOtpPayload): Promise<SendOtpResponse> => {
    return getApiClient().post(AUTH_ENDPOINTS.sendOtp, body);
  },

  /**
   * Verify OTP
   */
  verifyOtp: (body: VerifyOtpPayload): Promise<VerifyOtpResponse> => {
    return getApiClient().post(AUTH_ENDPOINTS.verifyOtp, body);
  },

  /**
   * Update user profile
   */
  updateProfile: (body: UpdateProfilePayload): Promise<UpdateProfileResponse> => {
    return getApiClient().put(AUTH_ENDPOINTS.updateProfile, body);
  },

  /**
   * Reset or change password
   */
  resetChangePassword: (
    body: ResetChangePasswordPayload
  ): Promise<ResetChangePasswordResponse> => {
    return getApiClient().post(AUTH_ENDPOINTS.resetPassword, body);
  },

  /**
   * Send OTP for forgot password
   */
  sendForgotPasswordOtp: (email: string): Promise<SendOtpResponse> => {
    const payload: ForgotPasswordSendOtpPayload = {
      email,
      otpType: OTP_TYPE.FORGOT_PASSWORD,
    };
    return getApiClient().post(AUTH_ENDPOINTS.sendOtp, payload);
  },

  /**
   * Verify OTP for forgot password
   */
  verifyForgotPasswordOtp: (
    email: string,
    otp: string
  ): Promise<VerifyOtpResponse> => {
    const payload: ForgotPasswordVerifyOtpPayload = {
      email,
      otpType: OTP_TYPE.FORGOT_PASSWORD,
      otp,
    };
    return getApiClient().post(AUTH_ENDPOINTS.verifyOtp, payload);
  },

  /**
   * Reset password (forgot password flow)
   */
  resetPassword: (newPassword: string): Promise<ResetPasswordResponse> => {
    const payload: ResetPasswordPayload = {
      isResetPassword: true,
      newPassword,
    };
    return getApiClient().post(AUTH_ENDPOINTS.resetPassword, payload);
  },

  /**
   * Change password (for authenticated users)
   */
  changePassword: (body: ChangePasswordPayload): Promise<ChangePasswordResponse> => {
    return getApiClient().post(AUTH_ENDPOINTS.changePassword, body);
  },
};

